import {
  COVER_MAP_SNAPSHOT_BACKGROUND,
  snapshotCanvasOntoBackground,
} from '@client/helpers/canvas-snapshot.helpers';
import { copySvgTextPresentationAttributes } from './svg-text-clone.helpers';

export const REPORT_SCROLL_ANCHOR_ATTR = 'data-report-scroll-anchor';
export const REPORT_PAGE_ATTR = 'data-report-page';

/** Cap so a 160px thumb is not captured at 3× device pixels. */
export const REPORT_THUMBNAIL_MAX_DEVICE_PIXELS = 2;

export type ReportThumbnailCanvasSize = {
  canvasWidth: number;
  canvasHeight: number;
};

type CapturePageToPngOptions = {
  cacheBust?: boolean;
  pixelRatio?: number;
  skipAutoScale?: boolean;
  backgroundColor?: string;
  canvasWidth?: number;
  canvasHeight?: number;
  fontEmbedCSS?: string;
  onclone?: (document: Document, element: HTMLElement) => void;
};

export type CapturePageToPng = (
  element: HTMLElement,
  options?: CapturePageToPngOptions,
) => Promise<string>;

export type CaptureReportPageThumbnailOptions = ReportThumbnailCanvasSize & {
  fontEmbedCSS?: string;
  toPng?: CapturePageToPng;
};

/**
 * Output pixel size for a thumbnail snapshot of a laid-out report page.
 * Uses the page's aspect ratio and the sidebar thumb width, capped at
 * {@link REPORT_THUMBNAIL_MAX_DEVICE_PIXELS}.
 * Returns `undefined` when any input is not a positive size.
 */
export const getReportThumbnailCanvasSize = ({
  pageWidthPx,
  pageHeightPx,
  thumbWidthPx,
  devicePixelRatio,
}: {
  pageWidthPx: number;
  pageHeightPx: number;
  thumbWidthPx: number;
  devicePixelRatio: number;
}): ReportThumbnailCanvasSize | undefined => {
  if (pageWidthPx <= 0 || pageHeightPx <= 0 || thumbWidthPx <= 0) return;
  const dpr = Math.min(
    Math.max(devicePixelRatio, 1),
    REPORT_THUMBNAIL_MAX_DEVICE_PIXELS,
  );
  const canvasWidth = Math.max(1, Math.round(thumbWidthPx * dpr));
  const canvasHeight = Math.max(
    1,
    Math.round((pageHeightPx / pageWidthPx) * canvasWidth),
  );
  return { canvasWidth, canvasHeight };
};

/**
 * Index of the report page that contains `target`, or `undefined` when the
 * event did not originate inside a {@link REPORT_SCROLL_ANCHOR_ATTR} child of
 * `container`.
 */
export const getReportScrollAnchorIndex = (
  target: EventTarget | null,
  container: HTMLElement,
): number | undefined => {
  if (!(target instanceof Element)) return;
  const anchor = target.closest(`[${REPORT_SCROLL_ANCHOR_ATTR}]`);
  if (!anchor || !container.contains(anchor)) return;
  const index = Number(anchor.getAttribute(REPORT_SCROLL_ANCHOR_ATTR));
  if (!Number.isInteger(index) || index < 0) return;
  return index;
};

/**
 * The 16:9 page box inside a scroll anchor — that node owns `container-type`
 * and the computed `cqw` sizes html-to-image should snapshot.
 */
export const getReportPageCaptureElement = (anchor: HTMLElement): HTMLElement =>
  anchor.querySelector<HTMLElement>(`[${REPORT_PAGE_ATTR}]`) ?? anchor;

/**
 * Width×height signature of the capture targets, used to wait until layout
 * (including the aspect-ratio transition) has stopped changing.
 */
export const getReportPagesLayoutSignature = (
  anchors: readonly HTMLElement[],
): string =>
  anchors
    .map((anchor) => {
      const el = getReportPageCaptureElement(anchor);
      return `${el.offsetWidth}x${el.offsetHeight}`;
    })
    .join('|');

/**
 * Pin the cloned page box to the live element's pixel size so `cqw` in the
 * html-to-image clone matches the on-screen layout.
 */
export const applyReportPageCloneLayout = (
  cloned: HTMLElement,
  source: HTMLElement,
): void => {
  cloned.style.width = `${source.offsetWidth}px`;
  cloned.style.height = `${source.offsetHeight}px`;
  copySvgTextPresentationAttributes(source, cloned);
};

const defaultToPng: CapturePageToPng = async (element, options) => {
  const { toPng } = await import('html-to-image');
  return toPng(element, options);
};

/**
 * Rasterize a mounted report page to a PNG data URL sized for the thumbnail
 * strip. Falls back to the first canvas (cover map) when HTML capture fails.
 * Returns `undefined` when the page has no layout box or cannot be read.
 */
export const captureReportPageThumbnail = async (
  element: HTMLElement,
  options: CaptureReportPageThumbnailOptions,
): Promise<string | undefined> => {
  if (element.offsetWidth <= 0 || element.offsetHeight <= 0) return;

  const toPng = options.toPng ?? defaultToPng;
  try {
    const dataUrl = await toPng(element, {
      cacheBust: false,
      pixelRatio: 1,
      skipAutoScale: true,
      backgroundColor: COVER_MAP_SNAPSHOT_BACKGROUND,
      canvasWidth: options.canvasWidth,
      canvasHeight: options.canvasHeight,
      fontEmbedCSS: options.fontEmbedCSS,
      onclone: (_document, cloned) => {
        applyReportPageCloneLayout(cloned, element);
      },
    });
    if (!dataUrl.startsWith('data:image')) return;
    return dataUrl;
  } catch {
    const canvas = element.querySelector('canvas');
    if (!canvas) return;
    return snapshotCanvasOntoBackground(canvas, COVER_MAP_SNAPSHOT_BACKGROUND);
  }
};
